import { envReducer, profileReducer, registrationReducer } from 'flowhawk-core';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import { createLogger } from 'redux-logger';

import allSagas from '../Saga';
import SagaManager from '../Saga/SagaManager';
import applicationReducer from './ApplicationActions';
import authReducer from './AuthActions';
import chatroomReducer from './ChatroomActions';
import loadingReducer from './LoadingActions';
import chatListReducer from './ChatListActions';
import chatMessageReducer from './ChatMessageActions';
import chatEditReducer from './ChatEditActions';
import donorReducer from './DonorActions';
import permissionReducer from './PermissionActions';
import formReducer from './FormActions';
import notificationReducer from './NotificationActions';
import networkReducer from './NetworkActions';
import teamReducer from './TeamActions';
import passwordResetReducer from './PasswordResetActions';
import followerEditReducer from './FollowerEditActions';
import addPeopleReducer from './AddPeopleActions';
import domoReducer from './DomoActions';
import recipientUploadReducer from './RecipientUploadActions';
import caseNotificationsReducer from './CaseNotificationActions';
import arrayMiddleware from './Middleware/ArrayMiddleware';

export const reducers = {
  application: applicationReducer,
  auth: authReducer,
  profile: profileReducer,
  registration: registrationReducer,
  loading: loadingReducer,
  chatList: chatListReducer,
  chatMessage: chatMessageReducer,
  chatroom: chatroomReducer,
  chatEdit: chatEditReducer,
  donor: donorReducer,
  permission: permissionReducer,
  forms: formReducer,
  notification: notificationReducer,
  network: networkReducer,
  team: teamReducer,
  passwordReset: passwordResetReducer,
  env: envReducer,
  followerEdit: followerEditReducer,
  addPeople: addPeopleReducer,
  domo: domoReducer,
  recipientUpload: recipientUploadReducer,
  caseNotifications: caseNotificationsReducer,
};

export const rootReducer = combineReducers(reducers);

export type RootState = ReturnType<typeof rootReducer>;

let sagaMonitor: any;

if (import.meta.env.MODE === 'development' && import.meta.env.VITE_ENABLE_SAGA_MONITOR === 'true') {
  const createSagaMonitor = (await import('@clarketm/saga-monitor')).default;
  // Create configuration for the saga monitor
  const sagaMonitorConfig = {
    level: 'log',
    effectTrigger: false,
    effectResolve: false,
    effectReject: true,
    actionDispatch: false,
  };
  sagaMonitor = createSagaMonitor(sagaMonitorConfig);
}

const sagaMiddleware = createSagaMiddleware({
  // rethrow the error to trigger the application ErrorBoundary
  onError: (error) => {
    throw error;
  },
  sagaMonitor,
});

const assembleStore = () => {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => {
      const middleware = getDefaultMiddleware({
        thunk: false,
        immutableCheck: false,
        serializableCheck: false,
      }).concat(arrayMiddleware, sagaMiddleware);

      if (import.meta.env.MODE === 'development') {
        const logger = createLogger({
          duration: true,
        });
        middleware.push(logger);
      }
      return middleware;
    },
  });

  // Kick off the root Saga
  SagaManager.run(sagaMiddleware, allSagas);

  if (import.meta.hot) {
    import.meta.hot.accept(async () => {
      store.replaceReducer(combineReducers(reducers) as unknown as Parameters<typeof store.replaceReducer>[0]);

      const newSagaModule = await import('../Saga');
      const newYieldedSagas = newSagaModule.default;

      SagaManager.cancel(store);
      const newSagaManagerModule = await import('../Saga/SagaManager');
      const newManager = newSagaManagerModule.default;
      newManager.run(sagaMiddleware, newYieldedSagas, true);
    });
  }

  return {
    store,
  };
};
type AssembleStore = ReturnType<typeof assembleStore>;
export type AppStore = AssembleStore['store'];

export default assembleStore;
